import { getApiBaseUrl } from '../../utils/appConfig';

const getBaseUrl = () => getApiBaseUrl();

const STOREFRONT_TOKEN_KEY = "bw_token";
const ADMIN_TOKEN_KEY = "bw_admin_token";
const CATALOG_TOKEN_SESSION_KEY = "bw_catalog_token";
const CATALOG_TOKEN_STORAGE_KEY = "bw_catalog_token";

let storefrontToken = null;
let adminToken = null;

function getConfigCatalogToken() {
  const token = window.__APP_CONFIG__?.CATALOG_API_TOKEN;
  return token && String(token).trim() ? String(token).trim() : null;
}

/** Persist catalog read token (localStorage survives admin/customer logout). */
export function saveCatalogReadToken(token) {
  if (!token) return;
  sessionStorage.setItem(CATALOG_TOKEN_SESSION_KEY, token);
  localStorage.setItem(CATALOG_TOKEN_STORAGE_KEY, token);
}

/** Last known token for storefront catalog reads. */
export function getCatalogReadToken() {
  return (
    getConfigCatalogToken() ||
    localStorage.getItem(CATALOG_TOKEN_STORAGE_KEY) ||
    sessionStorage.getItem(CATALOG_TOKEN_SESSION_KEY)
  );
}

/** Storefront customer session token. */
export function setAuthToken(token) {
  storefrontToken = token;
  if (token) {
    sessionStorage.setItem(STOREFRONT_TOKEN_KEY, token);
    saveCatalogReadToken(token);
  } else {
    sessionStorage.removeItem(STOREFRONT_TOKEN_KEY);
  }
}

/** Admin panel session token (separate from storefront). */
export function setAdminAuthToken(token) {
  adminToken = token;
  if (token) {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    saveCatalogReadToken(token);
  } else {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  }
}

/** Restore catalog token from storage or active sessions. */
export function ensureCatalogToken() {
  const existing = getCatalogReadToken();
  if (existing) return existing;

  const legacy =
    sessionStorage.getItem(ADMIN_TOKEN_KEY) ||
    sessionStorage.getItem(STOREFRONT_TOKEN_KEY) ||
    localStorage.getItem(ADMIN_TOKEN_KEY) ||
    localStorage.getItem(STOREFRONT_TOKEN_KEY);

  if (legacy) {
    saveCatalogReadToken(legacy);
    return legacy;
  }
  return getConfigCatalogToken();
}

/**
 * Ensure catalog API can authenticate (runs once on storefront load).
 * Uses saved token, config token, or optional catalog reader login.
 */
export async function bootstrapCatalogAccess() {
  const existing = ensureCatalogToken();
  if (existing) return existing;

  const username = window.__APP_CONFIG__?.CATALOG_READER_USERNAME;
  const password = window.__APP_CONFIG__?.CATALOG_READER_PASSWORD;
  if (!username || !password) return null;

  try {
    const baseUrl = getBaseUrl();
    const res = await fetch(`${baseUrl}/Auth/Login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ Username: username, Password: password }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || data?.success === false) return null;

    const token =
      data?.token || data?.Token || data?.accessToken ||
      data?.data?.token || data?.data?.Token;
    if (token) {
      saveCatalogReadToken(token);
      return token;
    }
  } catch {
    /* optional bootstrap */
  }
  return null;
}

async function fetchWithToken(endpoint, token, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const method = options.method || "GET";
  const body =
    options.data !== undefined ? JSON.stringify(options.data) : undefined;
  const response = await fetch(url, { method, headers, body });
  return parseResponse(response);
}

export function getAuthToken() {
  return storefrontToken || sessionStorage.getItem(STOREFRONT_TOKEN_KEY);
}

export function getAdminAuthToken() {
  return adminToken || sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export async function initializeAuth() {
  const token = sessionStorage.getItem(STOREFRONT_TOKEN_KEY);
  if (token) {
    storefrontToken = token;
    return token;
  }
  return null;
}

export async function initializeAdminAuth() {
  const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) {
    adminToken = token;
    return token;
  }
  return null;
}

async function parseResponse(response) {
  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    if (data?.errors && typeof data.errors === "object") {
      const messages = Object.entries(data.errors)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
        .join(" | ");
      throw new Error(messages || data?.title || `HTTP ${response.status}`);
    }
    const message =
      data?.message || data?.Message || data?.title || data?.Title || `HTTP ${response.status}`;
    throw new Error(message);
  }

  if (data?.success === false) {
    const message = data?.message || data?.Message || "Request failed.";
    throw new Error(message);
  }

  return { data };
}

export async function publicApiFetch(endpoint, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  const method = options.method || "GET";
  const body =
    options.data !== undefined ? JSON.stringify(options.data) : undefined;

  const response = await fetch(url, { method, headers, body });
  return parseResponse(response);
}

/** Storefront catalog reads — public first (no login), then saved token if needed. */
export async function catalogApiFetch(endpoint, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();

  if (method === 'GET') {
    try {
      return await publicApiFetch(endpoint, options);
    } catch {
      /* try authenticated catalog access below */
    }
  }

  await bootstrapCatalogAccess();

  const tokenCandidates = [
    getCatalogReadToken(),
    getAdminAuthToken(),
    getAuthToken(),
  ].filter(Boolean);

  const unique = [...new Set(tokenCandidates)];

  for (const token of unique) {
    try {
      return await fetchWithToken(endpoint, token, options);
    } catch {
      /* try next token */
    }
  }

  if (method !== 'GET') {
    try {
      return await publicApiFetch(endpoint, options);
    } catch {
      /* fall through */
    }
  }

  throw new Error('Catalog API unavailable');
}

export async function apiFetch(endpoint, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const token =
    options.useStorefrontAuth
      ? getAuthToken()
      : getAdminAuthToken() || getAuthToken();

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const method = options.method || "GET";
  const body =
    options.data !== undefined ? JSON.stringify(options.data) : undefined;

  const response = await fetch(url, { method, headers, body });
  return parseResponse(response);
}
